import { TFunction } from "i18next";
import { mapEntries } from "radashi";
import { z } from "zod";
import {
  BooleanFieldDefinition,
  isBooleanDefinition,
} from "./fieldDefinitions/getBooleanDefinition";
import { NumberFieldDefinition } from "./fieldDefinitions/getNumberDefinition";
import {
  isOptionsDefinition,
  OptionsFieldDefinition,
} from "./fieldDefinitions/getOptionsFieldDefinition";

export type FilterGroupsSchema = z.infer<typeof filterGroupsSchema>;

export type Group = Record<
  string,
  BooleanFieldDefinition | NumberFieldDefinition
>;

export function buildGroupSchema<T extends Group>(group: T) {
  return z.object(
    mapEntries(group, (key, value) => [key, createFieldSchema(value)]),
  ) as z.ZodObject<{
    [K in keyof T]: T[K] extends BooleanFieldDefinition
      ? z.ZodBoolean
      : z.ZodNumber;
  }>;
}

export function createNumberFieldSchema(
  numberField: NumberFieldDefinition,
  {
    maxMessage = "schema.tooLarge",
    minMessage = "schema.tooSmall",
    numberMessage = "schema.required",
  } = {},
) {
  return z
    .number(numberMessage)
    .min(numberField.min, minMessage)
    .max(numberField.max, maxMessage);
}

export function normalizeSchemaErrors<
  TError extends { message?: string } | undefined,
>(field: string, errors: TError[], t: TFunction) {
  return errors?.map((error) =>
    error?.message?.includes("schema.")
      ? t(`${field}.${error.message.replace("schema.", "")}.default`)
      : error?.message,
  );
}

export const filterGroupsSchema = z.record(
  z.string(),
  z.array(
    z.object({
      id: z.string(),
      value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
    }),
  ),
);

function createFieldSchema(
  field:
    BooleanFieldDefinition | NumberFieldDefinition | OptionsFieldDefinition,
) {
  if (isBooleanDefinition(field)) {
    return z.boolean();
  }

  if (isOptionsDefinition(field)) {
    return z.string().nullable();
  }

  return createNumberFieldSchema(field);
}
